/**
 * What needs attention right now, DERIVED from the fleet rather than pushed to us.
 *
 * There is no notification store, no unread state and no server-side feed, and this panel does
 * not pretend otherwise: every row is computed from the same deployment records the Fleet
 * Overview board renders, using the same helpers, so the bell and the board can never disagree.
 * When a real feed exists this is where it lands; until then a derived list is honest and a
 * hard-coded one would not be.
 *
 * THE THREE STATES ARE DELIBERATELY DISTINCT, and collapsing any two of them is the bug this
 * file is most likely to grow:
 *
 *   not connected  -- there is no API in the tree. We have not asked.
 *   could not read -- we asked and it failed. NOT an empty list (wf-12 U-12).
 *   nothing to report -- we asked, it answered, and the fleet is fine.
 *
 * Only the third one is good news, and it is the only one allowed to look like good news.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useOptionalFleetApi, needsReauthentication } from '../../api/index.ts'
import { itemsFor, RANK, type Item } from './attention.ts'

export function NotificationsPanel() {
  const api = useOptionalFleetApi()
  const [items, setItems] = useState<Item[] | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!api) return
    let live = true
    void (async () => {
      try {
        const deployments = await api.listDeployments()
        if (!live) return
        const now = new Date()
        const all = deployments
          .flatMap((d) => itemsFor(d, now))
          .sort((a, b) => RANK[a.severity] - RANK[b.severity])
        setItems(all)
        setError(null)
      } catch (caught: unknown) {
        if (!live) return
        setError(caught instanceof Error ? caught : new Error(String(caught)))
      }
    })()
    return () => {
      live = false
    }
  }, [api])

  if (!api) {
    return (
      <p className="popover-empty">
        This console is not connected to a fleet, so nothing has been checked. That is not the
        same as nothing needing attention.
      </p>
    )
  }

  if (error) {
    return (
      <p className="popover-empty popover-error">
        {needsReauthentication(error)
          ? 'Your session expired before the fleet could be read. Sign in again.'
          : `The fleet could not be read: ${error.message}`}
      </p>
    )
  }

  if (items === null) return <p className="popover-empty">Reading the fleet…</p>

  if (items.length === 0) {
    return <p className="popover-empty">Nothing needs attention. Every deployment is current and reporting.</p>
  }

  return (
    <ul className="popover-list">
      {items.map((item) => (
        <li key={item.id} className={`popover-item sev-${item.severity}`}>
          <Link to={item.href}>
            <span className="popover-item-title">{item.title}</span>
            <span className="popover-item-detail">{item.detail}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

